import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Formik, FormikValues } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { ModalProps } from "types/utils/modal";
import RefreshIcon from "static/images/RefreshIcon.svg";
import {
  TemplateTypes,
  useGetBroadcastTemplateQuery,
} from "store/apis/broadcast";
import { BroadcastMessageTemplateBody } from "./BroadcastMessageTemplateBody";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import classNames from "classnames";
import { ArrowRight } from "components/icons/ArrowRight";
import { capitalize } from "utils/capitalize";
import { useModal } from "hooks/useModal";

export const BroadcastMessageTemplates = ({
  isOpen,
  close,
  isBroadcastViaWhatsapp,
  isBroadcastViaEmail,
  templateFields,
  setTemplateFields,
  openSendBroadcast,
}: ModalProps & {
  isBroadcastViaWhatsapp: boolean;
  isBroadcastViaEmail: boolean;
  templateFields: Record<string, Record<string, any> | string>;
  setTemplateFields: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, any> | string>>
  >;
  openSendBroadcast: () => void;
}) => {
  const [selectedStates, setSelectedStates] = useState<TemplateTypes[]>([]);
  const [currentTemplateType, setCurrentTemplateType] = useState<number>(1);

  useEffect(() => {
    const selectedTemplates = ["chat"];
    if (isBroadcastViaWhatsapp) {
      selectedTemplates.push("whatsapp");
    }
    if (isBroadcastViaEmail) {
      selectedTemplates.push("email");
    }
    setSelectedStates(selectedTemplates as TemplateTypes[]);
  }, [isBroadcastViaWhatsapp, isBroadcastViaEmail]);

  const templateSteps = useMemo(() => {
    return selectedStates.map((template, index) => ({
      step: index + 1,
      name: template,
    }));
  }, [selectedStates]);

  const handleGoBack = () => {
    if (currentTemplateType > 1) {
      setCurrentTemplateType(currentTemplateType - 1);
    } else {
      close();
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xl">
        <Modal.Header>
          <div>
            <Modal.Title>Broadcast Message</Modal.Title>
            <Modal.Subtitle className="t-mt-2">
              {templateSteps.length > 1 && (
                <Stepper size="xs" direction="horizontal">
                  {templateSteps.map(({ step, name }) => (
                    <Stepper.Step
                      step={step}
                      key={step}
                      isActive={currentTemplateType >= step}
                      clickable={currentTemplateType >= step}
                      onClick={() => setCurrentTemplateType(step)}
                    >
                      <div
                        className={classNames(
                          "t-flex t-items-center t-text-body",
                          {
                            "t-text-text-30": currentTemplateType < step,
                            "t-text-purple": currentTemplateType >= step,
                          }
                        )}
                      >
                        {capitalize(name)}
                        {step !== templateSteps.length && (
                          <ArrowRight color="currentColor" />
                        )}
                      </div>
                    </Stepper.Step>
                  ))}
                </Stepper>
              )}
            </Modal.Subtitle>
          </div>
          <Modal.Close />
        </Modal.Header>
        <BroadcastMessageTemplateBody
          type={selectedStates[currentTemplateType - 1]}
          handleGoBack={handleGoBack}
          currentTemplateType={currentTemplateType}
          selectedStatesLength={selectedStates.length}
          setCurrentTemplateType={setCurrentTemplateType}
          openSendBroadcast={openSendBroadcast}
          setTemplateFields={setTemplateFields}
          templateFields={templateFields}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
